/**
 * Typed API client with JWT header injection.
 *
 * - Base URL comes from {@link config.apiBaseUrl} (VITE_API_BASE_URL or same-origin `/api`).
 * - The JWT (if present in localStorage) is injected as a `Bearer` Authorization header.
 * - A 401 response clears the stored token and redirects to `/login`.
 */

import { AUTH_TOKEN_KEY, config } from './config';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface RequestOptions extends Omit<RequestInit, 'method' | 'body'> {
  /** Skip Authorization header injection + 401 redirect (e.g. login call). */
  skipAuth?: boolean;
}

function getAuthToken(): string | null {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

interface ApiErrorBody {
  detail?: string | { msg?: string }[];
  message?: string;
}

/** A readable message from an error body; FastAPI validation errors are a list. */
export function errorMessage(body: ApiErrorBody, status: number): string {
  if (Array.isArray(body.detail)) {
    const messages = [...new Set(body.detail.map((d) => d.msg).filter(Boolean))];
    if (messages.length) return messages.join('; ');
  } else if (body.detail) {
    return body.detail;
  }
  return body.message ?? `HTTP ${status}`;
}

async function handleResponse<T>(response: Response, skipAuth: boolean): Promise<T> {
  if (response.status === 401 && !skipAuth) {
    try {
      localStorage.removeItem(AUTH_TOKEN_KEY);
    } catch {
      /* ignore storage errors */
    }
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
    throw new ApiError(401, 'Unauthorized');
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as ApiErrorBody;
    throw new ApiError(response.status, errorMessage(body, response.status));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

async function request<T>(
  endpoint: string,
  method: string,
  data?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const { skipAuth = false, headers, ...rest } = options;

  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((headers as Record<string, string> | undefined) ?? {}),
  };

  if (!skipAuth) {
    const token = getAuthToken();
    if (token) {
      requestHeaders['Authorization'] = `Bearer ${token}`;
    }
  }

  const response = await fetch(`${config.apiBaseUrl}${endpoint}`, {
    ...rest,
    method,
    headers: requestHeaders,
    body: data !== undefined ? JSON.stringify(data) : undefined,
  });

  return handleResponse<T>(response, skipAuth);
}

export const api = {
  get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return request<T>(endpoint, 'GET', undefined, options);
  },
  post<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>(endpoint, 'POST', data, options);
  },
  put<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>(endpoint, 'PUT', data, options);
  },
  patch<T>(endpoint: string, data?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>(endpoint, 'PATCH', data, options);
  },
  delete<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return request<T>(endpoint, 'DELETE', undefined, options);
  },
} as const;
